import { useQuery, type QueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import type { ExamPackageFeature } from '@/lib/entitlement-features';

export type EntitlementState = 'none' | 'purchased' | 'scheduled' | 'active' | 'expired' | 'trial';

/**
 * Canonical React Query key prefix for the entitlement-access query.
 *
 * MUST be the first element of the `useEntitlement` queryKey AND the key
 * passed to every `invalidateQueries` call that needs to refresh entitlement
 * state (after starting a trial, completing a purchase, or activating a cycle).
 *
 * History: this used to be the literal `'bar-package-access'`. Commit 4101d98b
 * renamed the query key to `'entitlement-access'` (to support both bar_package
 * and ube_package) but left ~11 `invalidateQueries(['bar-package-access'])`
 * call sites untouched. Because React Query matches keys by prefix, those
 * invalidations silently no-op'd — so after starting a trial the cached
 * `'none'` entitlement persisted for the full 5-minute staleTime, leaving the
 * "Start free trial" banner up and the practice gates locked until a manual
 * page reload. Centralizing the key here removes the drift class entirely.
 */
export const ENTITLEMENT_ACCESS_QUERY_KEY = 'entitlement-access' as const;

/**
 * Invalidate (and refetch active) the entitlement-access query for every
 * feature + user variant currently in the cache. Call this after any mutation
 * that changes the user's entitlement: `/pay/start-trial`, `/pay/create-payment`
 * success, `/pay/activate-cycle`. Prefer this over an inline `invalidateQueries`
 * so the key can never drift from `useEntitlement` again.
 */
export function invalidateEntitlementAccess(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: [ENTITLEMENT_ACCESS_QUERY_KEY] });
}

export interface CycleInfo {
  cycleName: string;
  cycleKey: string;
  cycleStartDate: string;
  examDate: string;
}

export interface BarPackageAccess {
  hasAccess: boolean;
  /** @deprecated Use hasAccess instead */
  hasBarPackage: boolean;
  entitlementState: EntitlementState;
  cycleInfo: CycleInfo | null;
  expiresAt: string | null;
  isLoading: boolean;
}

/**
 * Derive a human-readable cycle name from an exam_cycle key.
 * e.g. '2026-07' → 'July 2026', '2027-02' → 'February 2027'
 */
function deriveCycleName(examCycle: string): string {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  const parts = examCycle.split('-');
  if (parts.length >= 2) {
    const year = parts[0];
    const monthIndex = parseInt(parts[1], 10) - 1;
    if (monthIndex >= 0 && monthIndex < 12) {
      return `${months[monthIndex]} ${year}`;
    }
  }
  return examCycle;
}

/**
 * Extended row type for user_entitlements including cycle columns.
 * These columns may not yet exist in the generated types but will be
 * added by the activation migration. PostgREST will return them as
 * additional fields when present on the table.
 */
interface EntitlementRow {
  id: string;
  status: string | null;
  state: string | null;
  expires_at: string | null;
  exam_cycle?: string | null;
  cycle_start_date?: string | null;
  exam_date?: string | null;
}

/**
 * Generic entitlement access hook. Checks whether the current user holds a
 * `user_entitlements` row for the given exam-package feature and derives its
 * lifecycle state, cycle info, expiration, and access flag.
 *
 * `useBarPackageAccess` and `useUbePackageAccess` are thin wrappers over this.
 *
 * @param feature exact `user_entitlements.feature` value (e.g. 'bar_package' | 'ube_package')
 */
export function useEntitlement(feature: ExamPackageFeature): BarPackageAccess {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: [ENTITLEMENT_ACCESS_QUERY_KEY, feature, user?.id],
    queryFn: async () => {
      // Select base columns that exist in generated types.
      // Cycle columns (exam_cycle, cycle_start_date, exam_date) will be
      // returned by PostgREST when present on the table even though they
      // aren't in the generated TS types yet.
      // Exact-match on feature — never a prefix match (see entitlement-features.ts).
      const { data: rawData, error } = await supabase
        .from('user_entitlements')
        .select('id, status, state, expires_at, exam_cycle, cycle_start_date, exam_date')
        .eq('user_id', user!.id)
        .eq('feature', feature)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      if (!rawData) {
        return {
          hasAccess: false,
          entitlementState: 'none' as EntitlementState,
          cycleInfo: null,
          expiresAt: null,
        };
      }

      // Cast to our extended type to access cycle columns if they exist
      const data = rawData as unknown as EntitlementRow;

      // Map the lifecycle column to our EntitlementState type.
      // Prefer `state` (the newer lifecycle column added 2026-04-15) over legacy `status`.
      // Why: trial users are written with status='active' (CHECK-valid) + state='trial';
      // reading status would mis-classify them as 'active' and hide the trial banner.
      // Fall back to `status` for legacy rows where `state` is null.
      const lifecycleValue = (data.state ?? data.status ?? '') as string;
      let entitlementState: EntitlementState;
      if (lifecycleValue === 'active' || lifecycleValue === 'purchased' || lifecycleValue === 'scheduled' || lifecycleValue === 'expired' || lifecycleValue === 'trial') {
        entitlementState = lifecycleValue;
      } else {
        // Unknown state — treat as none
        entitlementState = 'none';
      }

      // Check expiration: if active or trial but expired, override state
      const isExpired = !!data.expires_at && new Date(data.expires_at) < new Date();
      if (isExpired && (entitlementState === 'active' || entitlementState === 'trial')) {
        entitlementState = 'expired';
      }

      const hasAccess = (entitlementState === 'active' || entitlementState === 'trial') && !isExpired;

      // Build cycle info if the user has selected an exam cycle
      let cycleInfo: CycleInfo | null = null;
      if (data.exam_cycle) {
        cycleInfo = {
          cycleName: deriveCycleName(data.exam_cycle),
          cycleKey: data.exam_cycle,
          cycleStartDate: data.cycle_start_date ?? '',
          examDate: data.exam_date ?? '',
        };
      }

      return {
        hasAccess,
        entitlementState,
        cycleInfo,
        expiresAt: data.expires_at ?? null,
      };
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    hasAccess: query.data?.hasAccess ?? false,
    hasBarPackage: query.data?.hasAccess ?? false,
    entitlementState: query.data?.entitlementState ?? 'none',
    cycleInfo: query.data?.cycleInfo ?? null,
    expiresAt: query.data?.expiresAt ?? null,
    isLoading: query.isLoading,
  };
}

/**
 * Check the current user's bar_package (NextGen / Studicata) entitlement.
 * Thin wrapper over useEntitlement('bar_package') — preserves the existing
 * public API consumed across NextGen surfaces.
 */
export function useBarPackageAccess(): BarPackageAccess {
  return useEntitlement('bar_package');
}

/**
 * Check the current user's ube_package (standalone UBE/MBE) entitlement.
 */
export function useUbePackageAccess(): BarPackageAccess {
  return useEntitlement('ube_package');
}
